import { type BuildMarker, BuildMarkerSchema } from '@playerbase/types';

import { supabase } from '../lib/supabase.js';
import { mapBuildMarkerRow } from '../shell/shell-data.js';

type RawRow = Parameters<typeof mapBuildMarkerRow>[0];

export type CreateBuildMarkerInput = {
  gameId: string;
  label: string;
  // Per-destination version. Manual creates default this to label until
  // an integration knows better; callers may pass it explicitly.
  version?: string;
  stageId: string;
  notes?: string | null;
  releasedAt?: string | null;
  createdByUserId?: string | null;
};

export type UpdateBuildMarkerPatch = {
  label?: string;
  version?: string;
  stageId?: string;
  notes?: string | null;
  releasedAt?: string | null;
};

function parseOne(raw: unknown): BuildMarker | null {
  const parsed = BuildMarkerSchema.safeParse(mapBuildMarkerRow(raw as RawRow));
  if (!parsed.success) {
    console.warn('[build-markers] Could not parse row.', parsed.error.issues);
    return null;
  }
  return parsed.data;
}

export async function createBuildMarker(
  input: CreateBuildMarkerInput,
): Promise<BuildMarker | null> {
  const releasedAt = input.releasedAt ?? new Date().toISOString();
  const { data, error } = await supabase
    .from('build_markers')
    .insert({
      game_id: input.gameId,
      label: input.label,
      version: input.version ?? input.label,
      stage_id: input.stageId,
      notes: input.notes ?? null,
      released_at: releasedAt,
      origin: 'manual',
      created_by_user_id: input.createdByUserId ?? null,
    })
    .select('*')
    .single();

  if (error) {
    console.warn('[build-markers] Failed to create:', error.message);
    return null;
  }
  return parseOne(data);
}

export async function updateBuildMarker(
  id: string,
  patch: UpdateBuildMarkerPatch,
): Promise<BuildMarker | null> {
  const update: Record<string, unknown> = {};
  if (patch.label !== undefined) update.label = patch.label;
  if (patch.version !== undefined) update.version = patch.version;
  if (patch.stageId !== undefined) update.stage_id = patch.stageId;
  if (patch.notes !== undefined) update.notes = patch.notes;
  if (patch.releasedAt !== undefined) update.released_at = patch.releasedAt;
  if (Object.keys(update).length === 0) return null;

  const { data, error } = await supabase
    .from('build_markers')
    .update(update)
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    console.warn('[build-markers] Failed to update:', error.message);
    return null;
  }
  return parseOne(data);
}

export async function archiveBuildMarker(id: string): Promise<BuildMarker | null> {
  const { data, error } = await supabase
    .from('build_markers')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    console.warn('[build-markers] Failed to archive:', error.message);
    return null;
  }
  return parseOne(data);
}

export async function unarchiveBuildMarker(id: string): Promise<BuildMarker | null> {
  const { data, error } = await supabase
    .from('build_markers')
    .update({ archived_at: null })
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    console.warn('[build-markers] Failed to unarchive:', error.message);
    return null;
  }
  return parseOne(data);
}
